import { useState } from 'react';
import { XMarkIcon, ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { config } from '../config/environment';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId?: string;
  componentId?: string;
  productName?: string;
  componentName?: string;
  initialType?: 'correction' | 'general_feedback' | 'data_improvement' | 'feature_request' | 'bug_report';
  initialField?: string;
  initialCurrentValue?: string;
}

interface FeedbackFormData {
  feedbackType: string;
  title: string;
  description: string;
  userEmail: string;
  userName: string;
  fieldName: string;
  currentValue: string;
  suggestedValue: string;
  sourceUrl: string;
  sourceDescription: string;
  confidenceLevel: number;
}

const FeedbackModal = ({
  isOpen,
  onClose,
  productId,
  componentId,
  productName,
  componentName,
  initialType = 'general_feedback',
  initialField = '',
  initialCurrentValue = ''
}: FeedbackModalProps) => {
  const [formData, setFormData] = useState<FeedbackFormData>({
    feedbackType: initialType,
    title: '',
    description: '',
    userEmail: '',
    userName: '',
    fieldName: initialField,
    currentValue: initialCurrentValue,
    suggestedValue: '',
    sourceUrl: '',
    sourceDescription: '',
    confidenceLevel: 3
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (field: keyof FeedbackFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_BASE = `${config.apiBaseUrl}/api`;
      
      let endpoint = `${API_BASE}/feedback/submit`;
      let payload: any = {
        feedback_type: formData.feedbackType,
        title: formData.title,
        description: formData.description,
        user_email: formData.userEmail || undefined,
        user_name: formData.userName || undefined,
        product_id: productId || undefined,
        component_id: componentId || undefined
      };

      // Add correction-specific fields
      if (formData.feedbackType === 'correction') {
        payload = {
          ...payload,
          field_name: formData.fieldName,
          current_value: formData.currentValue,
          suggested_value: formData.suggestedValue,
          source_url: formData.sourceUrl || undefined,
          source_description: formData.sourceDescription || undefined,
          confidence_level: formData.confidenceLevel
        };

        // Use specific correction endpoints if available
        if (productId) {
          endpoint = `${API_BASE}/feedback/products/${productId}/correct`;
          payload = {
            title: formData.title,
            description: formData.description,
            field_name: formData.fieldName,
            current_value: formData.currentValue,
            suggested_value: formData.suggestedValue,
            source_url: formData.sourceUrl || undefined,
            source_description: formData.sourceDescription || undefined,
            confidence_level: formData.confidenceLevel,
            user_email: formData.userEmail || undefined,
            user_name: formData.userName || undefined
          };
        } else if (componentId) {
          endpoint = `${API_BASE}/feedback/components/${componentId}/correct`;
          payload = {
            title: formData.title,
            description: formData.description,
            field_name: formData.fieldName,
            current_value: formData.currentValue,
            suggested_value: formData.suggestedValue,
            source_url: formData.sourceUrl || undefined,
            source_description: formData.sourceDescription || undefined,
            confidence_level: formData.confidenceLevel,
            user_email: formData.userEmail || undefined,
            user_name: formData.userName || undefined
          };
        }
      } else if (formData.feedbackType === 'general_feedback') {
        endpoint = `${API_BASE}/feedback/general`;
        payload = {
          title: formData.title,
          description: formData.description,
          feedback_type: formData.feedbackType,
          user_email: formData.userEmail || undefined,
          user_name: formData.userName || undefined
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      await response.json();
      
      setSubmitSuccess(true);
      toast.success('Thank you for your feedback!');
      
      // Reset form
      setFormData({
        feedbackType: 'general_feedback',
        title: '',
        description: '',
        userEmail: '',
        userName: '',
        fieldName: '',
        currentValue: '',
        suggestedValue: '',
        sourceUrl: '',
        sourceDescription: '',
        confidenceLevel: 3
      });

      // Close modal after a delay
      setTimeout(() => {
        setSubmitSuccess(false);
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const isCorrection = formData.feedbackType === 'correction';
  const entityName = productName || componentName || 'this item';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isCorrection ? `Submit Correction for ${entityName}` : 'Submit Feedback'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {submitSuccess ? (
          <div className="p-6 text-center">
            <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Thank You!</h3>
            <p className="text-gray-600">
              Your feedback has been submitted successfully. We'll review it and get back to you if needed.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Feedback Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type of Feedback
              </label>
              <select
                value={formData.feedbackType}
                onChange={(e) => handleInputChange('feedbackType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="correction">Data Correction</option>
                <option value="general_feedback">General Feedback</option>
                <option value="data_improvement">Data Improvement Suggestion</option>
                <option value="feature_request">Feature Request</option>
                <option value="bug_report">Bug Report</option>
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder={isCorrection ? "Brief description of the correction" : "Brief summary of your feedback"}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={isCorrection ? "Explain what's incorrect and why" : "Provide details about your feedback"}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Correction-specific fields */}
            {isCorrection && (
              <>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <ExclamationCircleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <strong>Help us verify:</strong> Please provide sources or references to support your correction. 
                      This helps us maintain data quality and accuracy.
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Field Being Corrected <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.fieldName}
                      onChange={(e) => handleInputChange('fieldName', e.target.value)}
                      placeholder="e.g., manufacturing_cost, carbon_footprint_kg"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confidence Level <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.confidenceLevel}
                      onChange={(e) => handleInputChange('confidenceLevel', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value={1}>1 - Low confidence</option>
                      <option value={2}>2 - Some confidence</option>
                      <option value={3}>3 - Moderate confidence</option>
                      <option value={4}>4 - High confidence</option>
                      <option value={5}>5 - Very high confidence</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current (Incorrect) Value <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.currentValue}
                      onChange={(e) => handleInputChange('currentValue', e.target.value)}
                      placeholder="What the system currently shows"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Correct Value <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.suggestedValue}
                      onChange={(e) => handleInputChange('suggestedValue', e.target.value)}
                      placeholder="What the correct value should be"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source URL
                  </label>
                  <input
                    type="url"
                    value={formData.sourceUrl}
                    onChange={(e) => handleInputChange('sourceUrl', e.target.value)}
                    placeholder="https://example.com/source-document"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source Description
                  </label>
                  <textarea
                    value={formData.sourceDescription}
                    onChange={(e) => handleInputChange('sourceDescription', e.target.value)}
                    placeholder="Describe your source (e.g., official company report, industry analysis, etc.)"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            )}

            {/* Optional contact information */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-4">
                Contact Information (Optional)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={formData.userName}
                    onChange={(e) => handleInputChange('userName', e.target.value)}
                    placeholder="Your name (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Email
                  </label>
                  <input
                    type="email"
                    value={formData.userEmail}
                    onChange={(e) => handleInputChange('userEmail', e.target.value)}
                    placeholder="your.email@example.com (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                We'll only use your contact information to follow up on your feedback if needed.
              </p>
            </div>

            {/* Submit buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal; 